/* eslint-disable no-restricted-globals */
/* eslint-disable import/no-extraneous-dependencies */
import * as dd from 'dingtalk-jsapi';
import { isIos } from '@ibiz-template/runtime';
import { useViewStack } from '../util';
import { MobPlatformProviderBase } from './mob-platform-provider-base';

type IConfigParams = {
  /**
   * @description 企业内部应用的agentId
   * @type {string}
   */
  agentId: string;

  /**
   * @description 企业ID
   * @type {string}
   */
  corpId: string;

  /**
   * @description 签名时间戳
   * @type {string}
   */
  timeStamp: string;

  /**
   * @description 随机字符串
   * @type {string}
   */
  nonceStr: string;

  /**
   * @description 签名
   * @type {string}
   */
  signature: string;

  /**
   * @description 授权API集合
   * @type {string[]}
   */
  jsApiList: string[];
};

/**
 * @description 钉钉搭载平台适配器
 * @export
 * @class DingTalkPlatformProvider
 * @extends {MobPlatformProviderBase}
 */
export class DingTalkPlatformProvider extends MobPlatformProviderBase {
  /**
   * @description 获取配置参数
   * @private
   * @returns {*}  {(Promise<IConfigParams | undefined>)}
   * @memberof DingTalkPlatformProvider
   */
  private async getConfigParams(): Promise<IConfigParams | undefined> {
    // 当前应用路径
    // const url = window.location.href.split('#')[0];
    // TODO 发送后台获取钉钉权限校验参数
    return undefined;
  }

  /**
   * @description 初始化
   * @returns {*}  {Promise<void>}
   * @memberof DingTalkPlatformProvider
   */
  async init(): Promise<void> {
    if (isIos()) {
      this.initViewport();
    }
    if (!dd) return;
    const config = await this.getConfigParams();
    if (config) dd.config(config);
  }

  /**
   * @description 设置浏览器标签页标题
   * @param {string} title
   * @memberof PlatformProviderBase
   */
  setBrowserTitle(title: string): void {
    const app = ibiz.hub.getApp();
    let tabTitle: string =
      ibiz.env.AppLabel || app.model.title || this.sourceTitle;
    if (!ibiz.config.mob.mobShowAppTitle) {
      tabTitle = title;
    } else if (title) {
      tabTitle = `${tabTitle} - ${title}`;
    }
    if (dd) {
      dd.setNavigationTitle({
        title: tabTitle,
      });
    } else {
      super.setBrowserTitle(title);
    }
  }

  /**
   * 返回
   *
   * @memberof DingTalkPlatformProvider
   */
  back(): void {
    const { goBack } = useViewStack();
    if (dd) {
      dd.goBackPage({
        fail: (res: IData) => {
          // 如果当前环境没有 goBackPage 则走默认的预置的返回逻辑
          if (res.errorMessage === 'API not exists') goBack();
        },
      });
    } else {
      goBack();
    }
  }

  getShowViewHeader(): boolean {
    return true;
  }

  getShowPresetBack(): boolean {
    return false;
  }

  /**
   * @description 计算底部安全距离
   * @memberof DingTalkPlatformProvider
   */
  calcSafeBottom(): void {
    // 延迟执行，确保钉钉容器渲染完成
    setTimeout(() => {
      const getSafeAreaBottom = (): number => {
        // 通过 CSS env 获取安全区域高度
        const div = document.createElement('div');
        div.style.paddingBottom = 'env(safe-area-inset-bottom)';
        document.body.appendChild(div);
        const bottom =
          parseInt(window.getComputedStyle(div).paddingBottom, 10) || 0;
        document.body.removeChild(div);
        return bottom;
      };

      // 针对全面屏 iPhone，额外处理底部安全距离
      const safeBottom = getSafeAreaBottom();
      document.body.style.setProperty(
        '--safe-area-inset-bottom',
        `${safeBottom}px`,
      );
    }, 300);
  }

  /**
   * @description 适配钉钉中ios机型
   * @memberof DingTalkPlatformProvider
   */
  adaptDingTalkIOS(): void {
    // 钉钉 WebView 首次 viewport 计算是延迟的，默认设置安全距离为0
    setTimeout(() => {
      document.body.style.setProperty('--safe-area-inset-bottom', `${0}px`);
    }, 300);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        document.body.style.height = `${window.visualViewport!.height}px`;
        this.fixViewport();
        this.calcSafeBottom();
      });
      window.visualViewport.addEventListener('scroll', () => {
        this.fixViewport();
      });
      this.fixViewport();
    }
  }

  /**
   * @description 初始化视窗
   * @memberof DingTalkPlatformProvider
   */
  initViewport(): void {
    // 页面加载完成后执行
    if (document.readyState === 'complete') {
      this.adaptDingTalkIOS();
    } else {
      window.addEventListener('load', this.adaptDingTalkIOS);
    }
  }

  /**
   * 修复视窗变化（修复ios软键盘弹出导致visualViewport高度计算异常，导致页面整体上移）
   *
   * @memberof DingTalkPlatformProvider
   */
  fixViewport(): void {
    const vv = window.visualViewport;
    if (vv) {
      // 处理输入框聚焦时顶部遮挡bug
      document.documentElement.style.setProperty(
        '--visual-viewport-offset-top',
        `${vv.offsetTop}px`,
      );
    }
  }
}
